"use client";

import React, { useEffect, useRef, useState } from "react";
import { motion, Variants } from "framer-motion";

interface CardItem {
  title: string;
  description: string | string[];
}

interface Card {
  title: string;
  description?: string;
  items?: CardItem[];
  belowDescription?: string;
}

interface NewDescriptionCardProps {
  cards: Card[];
  columns?: 2 | 3 | 4 | 5 | 6;
}

const NewDescriptionCard: React.FC<NewDescriptionCardProps> = ({ cards, columns = 3 }) => {
  const columnClassMap: Record<number, string> = {
    2: "sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2",
    3: "sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3",
    4: "sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4",
    5: "sm:grid-cols-2 lg:grid-cols-5 xl:grid-cols-5",
    6: "sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6",
  };

  const topSectionRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [rowTopHeights, setRowTopHeights] = useState<number[]>([]);

  // Equal height per row
  useEffect(() => {
    if (!topSectionRefs.current.length) return;

    const heights: number[] = [];
    const rowCount = Math.ceil(cards.length / columns);

    for (let r = 0; r < rowCount; r++) {
      const start = r * columns;
      const end = start + columns;
      const rowCards = topSectionRefs.current.slice(start, end);
      const maxHeight = Math.max(...rowCards.map((el) => (el ? el.offsetHeight : 0)));
      heights.push(maxHeight);
    }

    setRowTopHeights(heights);
  }, [cards, columns]);

  // Animation variants
  const cardVariants: Variants = {
    offscreen: (index: number) => ({
      opacity: 0,
      x: index % 2 === 0 ? -50 : 50,
      y: 50,
      scale: 0.9,
    }),
    onscreen: {
      opacity: 1,
      x: 0,
      y: 0,
      scale: 1,
      transition: {
        type: "spring",
        bounce: 0.2,
        duration: 0.6,
      },
    },
  };

  const renderCard = (card: Card, index: number) => {
    const rowIndex = Math.floor(index / columns);
    const rowHeight = rowTopHeights[rowIndex] || "auto";

    return (
      <motion.div
        key={index}
        custom={index}
        initial="offscreen"
        whileInView="onscreen"
        exit="offscreen" // Animate back when leaving viewport
        viewport={{ once: false, amount: 0.2 }}
        variants={cardVariants}
        className="bg-white flex flex-col overflow-hidden border border-gray-200 h-full"
      >
        {/* Top section */}
        <div
          ref={(el: HTMLDivElement | null) => {
            if (el) {
              topSectionRefs.current[index] = el;
            }
          }}


          className="p-4 md:p-8 border-b border-gray-200 flex flex-col justify-center"
          style={{ minHeight: rowHeight }}
        >
          <h3 className="text-md sm:text-lg xl:text-xl font-semibold text-[#272d57] leading-tight">
            {card.title}
          </h3>
          {card.description && (
            <p className="text-sm sm:text-base text-[#1b1b1b] mt-2">{card.description}</p>
          )}
        </div>

        {/* Bottom section */}
        <div className="px-4 py-2 sm:py-4 section-bg-white-soft flex-1 flex flex-col">
          {card.items && card.items.length > 0 && (
            <ul className="mt-1 space-y-1 sm:space-y-4">
              {card.items.map((item, idx) => (
                <li key={idx} className="flex flex-col gap-2 py-2 p-4">
                  <span className="text-red-500 font-semibold">{item.title}:</span>
                  <div className="flex flex-wrap gap-x-2 gap-y-2">
                    {Array.isArray(item.description)
                      ? item.description.map((point, i) => (
                          <span
                            key={i}
                            className="border border-[#d1d5db]/48 capitalize text-xs sm:text-sm p-1 px-2 bg-white -skew-x-12 relative overflow-hidden animate-border-glow-light"
                          >
                            <span className="transform skew-x-12">{point}</span>
                          </span>
                        ))
                      : item.description}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </motion.div>
    );
  };

  return (
    <div className="flex flex-col gap-5">
      <div className={`grid grid-cols-1 ${columnClassMap[columns]} gap-5 items-stretch`}>
        {cards.map((card, index) => renderCard(card, index))}
      </div>
    </div>
  );
};

export default NewDescriptionCard;